import { css } from "@emotion/react";
import { SwarovskiOptikSegmentShort } from "@swo/shared";
import { FullTextSearch, SwarovskiOptikLocaleFrontend, type SearchPageTranslation } from "@swo/storefront";
import cn from "classnames";
import { either } from "fp-ts";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { ContentContainer } from "../../../components/_shared-components/arrangement/Container";
import { LoadingStateOps } from "../../../domain/data-layer/loading-state";
import { LoadingStateWithIdleOps } from "../../../domain/data-layer/loading-state-with-idle";
import { storefrontClient } from "../../../domain/storefront";
import { interpolateLinearly } from "../../../domain/translations/simple-translations-module";
import { Translated, t2 } from "../../../domain/translations/translations";
import { useEqualState, useSmoothScrolling } from "../../../hooks";
import { useResourceRelatedInfo } from "../../../hooks/use-resource-related-info";
import { SmallLoadingSpinner } from "../../../routes/modal-loading-spinner";
import { FONTS, fontSize } from "../../../styles/fonts";
import { CSS_COLORS, CSS_SPACER_SIZES } from "../../../styles/styles-css-variables";
import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { SearchPageSearchInput } from "./search-results";
import { elementIDs } from "./search-results-ids";
import { SearchResultsSummary } from "./search-results-summary";
import { SearchResultsView } from "./search-results-view";
import { T } from "../../../components/T";

export type SearchT = Translated<SearchPageTranslation>;

export const SearchRootComponent: ReactTFC<{
  locale: SwarovskiOptikLocaleFrontend;
  segment: SwarovskiOptikSegmentShort;
  translations: SearchPageTranslation;
}> = ({ locale, segment, translations }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const t = t2(translations);

  const locationSearch = new URLSearchParams(location.search);
  const activeQuery = locationSearch.get("q");

  const [searchText, setSearchText] = useState<string>(activeQuery || "");
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    void navigate({ search: getSearchParam(searchText) });
  };

  const onClickRemove = () => {
    setSearchText("");
  };

  const resultsLs = useResourceRelatedInfo<string, string, string, { query: string; response: FullTextSearch }>(
    activeQuery && activeQuery.length > 0 ? LoadingStateOps.asLoaded(activeQuery) : LoadingStateWithIdleOps.idleS,
    r => r,
    e => (e.length > 0 ? either.right(e) : either.left("query-too-short")),
    data => {
      return storefrontClient.fullTextSearch(locale.code, segment.key, data).then(results => {
        return { query: data, response: results };
      });
    },
  );

  function getSearchParam(query: string): `?${string}` {
    const params = new URLSearchParams({ q: query });
    const paramsStr = params.toString();

    return `?${paramsStr}`;
  }

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const { current } = ref;

    if (current) {
      current.focus();
    }
  }, []);

  useSmoothScrolling(true);

  const totalLs = LoadingStateWithIdleOps.mapI(resultsLs, ({ result }) => {
    const { downloads, products, stories, videoStories } = result.response;

    return {
      totalCount: downloads.length + products.length + stories.length + videoStories.length,
      summary: [
        {
          id: elementIDs.products,
          text: `${products.length} ${t("section.products").value}`,
          count: products.length,
        },
        {
          id: elementIDs.videoStories,
          text: `${videoStories.length} ${t("section.videos").value}`,
          count: videoStories.length,
        },
        {
          id: elementIDs.stories,
          text: `${stories.length} ${t("section.stories").value}`,
          count: stories.length,
        },
        {
          id: elementIDs.downloads,
          text: `${downloads.length} ${t("section.downloads").value}`,
          count: downloads.length,
        },
      ],
    };
  });

  return (
    <ContentContainer
      className={cn("expand", typeof activeQuery === "undefined" ? "state-empty" : "state-with-results", {
        "results-loaded": resultsLs.__tag === "loaded" && resultsLs.data.result.query === activeQuery,
      })}
      attachStyles={wrapperStyles}
    >
      <SearchPageSearchInput
        ref={ref}
        searchText={searchText}
        setSearchText={setSearchText}
        onSubmit={onSubmit}
        onClickRemove={onClickRemove}
        t={t}
      />

      {!activeQuery && (
        <span
          css={[
            FONTS.SwarovskiFutura300,
            fontSize.x2,
            css`
              ${CSS_COLORS.Grey3};

              grid-row: 2 / span 3;
              align-self: center;
              text-align: center;
            `,
          ]}
        >
          <T>{t("enterYourQuery")}</T>
        </span>
      )}

      {resultsLs.__tag === "loading" ? (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            grid-row: 2 / span 3;
          `}
        >
          <SmallLoadingSpinner color={"green"} size={"2x"} noMargin />
        </div>
      ) : null}

      {resultsLs.__tag === "loaded" && resultsLs.data.result.query === activeQuery && totalLs.__tag === "loaded" ? (
        <>
          <SearchResultsSummary
            text={interpolateLinearly(t("resultsInfo").value, [
              totalLs.data.totalCount.toString(),
              resultsLs.data.result.query,
            ])}
            summary={totalLs.data.summary}
            totalCount={totalLs.data.totalCount}
          />
          <SearchResultsView
            searchResult={resultsLs.data.result.response}
            query={resultsLs.data.result.query}
            locale={locale}
            totalCount={totalLs.data.totalCount}
            t={t}
          />
        </>
      ) : null}
    </ContentContainer>
  );
};

const wrapperStyles = css`
  min-height: 70vh;

  display: grid;
  grid-template-columns: 1fr;

  &.state-empty {
    grid-template-rows: 1fr auto 1fr;
  }

  &.state-with-results {
    grid-template-rows: auto auto auto 1fr;

    &.results-loaded > ._summary {
      margin-top: 30px;
      margin-bottom: ${CSS_SPACER_SIZES.mdlg};
    }
  }

  justify-content: center;
`;
